import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import type { WarehouseBreadcrumbItem } from "src/container/Warehouse/components/WarehouseBreadcrumb";
import WarehouseBreadcrumb from "src/container/Warehouse/components/WarehouseBreadcrumb";
import WarehouseResourceGrid from "src/container/Warehouse/components/WarehouseResourceGrid";
import { hierarchyConfig } from "src/container/Warehouse/hierarchyConfig";
import {
  getResourcesFromApi
} from "src/container/Warehouse/warehouseHierarchy.util";
import { useQueryGetWarehouseChildren } from "src/queries/warehouses.queries";
import type {
  WarehouseResource,
  WarehouseResourcePayload,
  WarehouseResourceType,
} from "src/services/warehouseService";
import CustomSharedTitleSection from "../../../sipli-fleet-core/components/CustomSharedTitleSection/CustomSharedTitleSection";

interface HierarchyPageConfig {
  resource: Exclude<WarehouseResourceType, "warehouse">;
  parentIdParam: string;
  parentPayloadKey: keyof WarehouseResourcePayload;
  backPath: (params: Record<string, string | undefined>) => string;
  childPath?: (
    params: Record<string, string | undefined>,
    record: WarehouseResource,
  ) => string;
  extraPayload?: (
    params: Record<string, string | undefined>,
  ) => Partial<WarehouseResourcePayload>;
}

type HierarchyPage = "area" | "lane" | "shelf" | "position";

const hierarchyPages: Record<HierarchyPage, HierarchyPageConfig> = {
  area: {
    resource: "area",
    parentIdParam: "warehouseId",
    parentPayloadKey: "warehouseId",
    backPath: () => "/warehouse-management/warehouse",
    childPath: (params, record) =>
      `/warehouse-management/lane/${params.warehouseName}/${params.warehouseId}/${encodeURIComponent(
        record.name || String(record.id),
      )}/${record.id}`,
  },
  lane: {
    resource: "lane",
    parentIdParam: "areaId",
    parentPayloadKey: "areaId",
    backPath: (params) =>
      `/warehouse-management/area/${params.warehouseName}/${params.warehouseId}`,
    childPath: (params, record) =>
      `/warehouse-management/self/${params.warehouseName}/${params.warehouseId}/${params.areaName}/${params.areaId}/${encodeURIComponent(
        record.name || String(record.id),
      )}/${record.id}`,
    extraPayload: (params) => ({ warehouseId: params.warehouseId }),
  },
  shelf: {
    resource: "shelf",
    parentIdParam: "laneId",
    parentPayloadKey: "laneId",
    backPath: (params) =>
      `/warehouse-management/lane/${params.warehouseName}/${params.warehouseId}/${params.areaName}/${params.areaId}`,
    childPath: (params, record) =>
      `/warehouse-management/position/${params.warehouseName}/${params.warehouseId}/${params.areaName}/${params.areaId}/${params.laneName}/${params.laneId}/${encodeURIComponent(
        record.name || String(record.id),
      )}/${record.id}`,
    extraPayload: (params) => ({
      warehouseId: params.warehouseId,
      areaId: params.areaId,
    }),
  },
  position: {
    resource: "position",
    parentIdParam: "shelfId",
    parentPayloadKey: "shelfId",
    backPath: (params) =>
      `/warehouse-management/self/${params.warehouseName}/${params.warehouseId}/${params.areaName}/${params.areaId}/${params.laneName}/${params.laneId}`,
    extraPayload: (params) => ({
      warehouseId: params.warehouseId,
      areaId: params.areaId,
      laneId: params.laneId,
    }),
  },
};

const getDecodedParam = (
  params: Record<string, string | undefined>,
  key: string,
) => {
  const value = params[key];
  return value ? decodeURIComponent(value) : undefined;
};

const buildWarehouseBreadcrumbs = (
  params: Record<string, string | undefined>,
  page: HierarchyPage,
  warehouseLabel: string,
): WarehouseBreadcrumbItem[] => {
  const breadcrumbs: WarehouseBreadcrumbItem[] = [
    {
      label: warehouseLabel,
      path: "/warehouse-management/warehouse",
    },
  ];
  const warehouseName = getDecodedParam(params, "warehouseName");
  const areaName = getDecodedParam(params, "areaName");
  const laneName = getDecodedParam(params, "laneName");
  const shelfName = getDecodedParam(params, "shelfName");

  if (warehouseName) {
    breadcrumbs.push({
      label: warehouseName,
      path:
        page === "area"
          ? undefined
          : `/warehouse-management/area/${params.warehouseName}/${params.warehouseId}`,
    });
  }

  if (areaName) {
    breadcrumbs.push({
      label: areaName,
      path:
        page === "lane"
          ? undefined
          : `/warehouse-management/lane/${params.warehouseName}/${params.warehouseId}/${params.areaName}/${params.areaId}`,
    });
  }

  if (laneName) {
    breadcrumbs.push({
      label: laneName,
      path:
        page === "shelf"
          ? undefined
          : `/warehouse-management/self/${params.warehouseName}/${params.warehouseId}/${params.areaName}/${params.areaId}/${params.laneName}/${params.laneId}`,
    });
  }

  if (shelfName) {
    breadcrumbs.push({
      label: shelfName,
    });
  }

  return breadcrumbs;
};

const WarehouseHierarchy = ({
  page,
}: {
  page: HierarchyPage;
}) => {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const pageConfig = hierarchyPages[page];
  const config = hierarchyConfig[pageConfig.resource];
  const parentId = params[pageConfig.parentIdParam];
  const query = useQueryGetWarehouseChildren(
    pageConfig.resource,
    parentId,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] =
    useState<WarehouseResource | null>(null);
  const resources = getResourcesFromApi(query.data?.data);
  const parentPayload = {
    [pageConfig.parentPayloadKey]: parentId,
    ...pageConfig.extraPayload?.(params),
  } as Partial<WarehouseResourcePayload>;

  const closeModal = () => {
    setModalOpen(false);
    setEditingRecord(null);
  };

  return (
    <section className="min-h-full pb-8 pl-2">
      <CustomSharedTitleSection
        titleKey={config.pluralKey}
        subTitle={<WarehouseBreadcrumb items={buildWarehouseBreadcrumbs(params, page, t("warehouse"))} /> }
        buttonKey={config.addKey}
        onClick={() => {
         setEditingRecord(null);
          setModalOpen(true);
        }}
        buttonProps={{ className: "!w-[182px] !h-[40px] !text-gray2" }}
        titleClassName="!text-[25.23px] !font-[700] !leading-[18px"
        classNameContainer="items-start"
      />

      <WarehouseResourceGrid
        resource={pageConfig.resource}
        resources={resources}
        isLoading={query.isLoading}
        isError={query.isError}
        parentPayload={parentPayload}
        modalOpen={modalOpen}
        editingRecord={editingRecord}
        onModalClose={closeModal}
        onEdit={(record) => {
          setEditingRecord(record);
          setModalOpen(true);
        }}
        onOpen={
          pageConfig.childPath
            ? (record) => navigate(pageConfig.childPath?.(params, record) ?? "")
            : undefined
        }
      />
    </section>
  );
};

export default WarehouseHierarchy;
